import React, { FC, useCallback } from 'react';
import { Formik } from 'formik';

import { SelectOption } from 'QuoteApp/services/endpoints/GetConfig';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import { FlexiblePaymentFrequency } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { formatText, getWeekDay, toCamelCase, toSnakeCase } from 'Shared/utils';

import { SelectorContainer } from '../selectors';
import {
  buildBiweeklyDateSelectorData,
  buildMonthlySelectorData,
  buildSemiMonthlySelectorData,
  buildWeekdaySelectorData,
  getConfigByFrequency,
} from '../utils';
import { validationSchema } from '../schema';
import { PriceContext, PriceCustomizerFormValues, PriceState } from '../domain';
import { fromInputToOutput, fromOutputToInput } from '../mapper';
import styles from './styles.module.css';

const FREQUENCY_LABELS: Record<FlexiblePaymentFrequency, string> = {
  weekly: 'Weekly',
  biWeekly: 'Every two weeks',
  semiMonthly: 'Twice a month',
  monthly: 'Once a month',
  fullPay: 'Pay in full',
};

export const PricePlanConfigurator: FC = () => {
  const {
    details,
    config,
    fetchRate,
    refreshDetails,
    rateLoading,
    detailsLoading,
    api,
  } = useAppContext();
  const { getSignaturePacket } = usePurchaseContext();

  const getListOfFrequencies = () => {
    if (!config?.flexiblePayments?.paymentFrequencies) return [];

    const { paymentFrequencies } = config.flexiblePayments;

    return paymentFrequencies.map(item => toCamelCase(item.name));
  };

  const buildFrequencySelectorData = (): Array<SelectOption<string>> => {
    const frequencies = getListOfFrequencies();

    if (frequencies.indexOf('fullPay') === -1) {
      frequencies.push('fullPay');
    }

    const supportedFrequencies = frequencies.filter(frequency => {
      return (
        FREQUENCY_LABELS[frequency as FlexiblePaymentFrequency] !== undefined
      );
    });

    return supportedFrequencies.map(frequency => {
      return {
        label: FREQUENCY_LABELS[frequency as FlexiblePaymentFrequency],
        value: frequency,
      };
    });
  };

  // Weekly default based on the effective date
  const getDefaultWeeklyPaymentDay = (effectiveStartDate: string) => {
    const effectiveDate = new Date(effectiveStartDate);

    if (isNaN(effectiveDate.getTime())) return undefined;

    const weekdayConfig = getConfigByFrequency(
      'weekly',
      config?.flexiblePayments?.paymentFrequencies
    ) as Array<SelectOption<string>>;

    const effectiveWeekDay = formatText(getWeekDay(effectiveDate), 'lowercase');

    const match = weekdayConfig.find(
      option => option.value === effectiveWeekDay
    );

    if (!match) return undefined;

    return match.value;
  };

  // Biweekly default based on the effective date
  const getDefaultBiweeklyPaymentDay = (effectiveStartDate: string) => {
    const effectiveDate = new Date(effectiveStartDate);

    if (isNaN(effectiveDate.getTime())) return undefined;

    const weekdayConfig = getConfigByFrequency(
      'biWeekly',
      config?.flexiblePayments?.paymentFrequencies
    ) as Array<SelectOption<string>>;

    const effectiveWeekDay = formatText(getWeekDay(effectiveDate), 'lowercase');

    const match = weekdayConfig.find(
      option => option.value === effectiveWeekDay
    );

    if (!match) return undefined;

    return match.value;
  };

  // Semi-monthly default based on the effective date
  const getDefaultSemiMonthlyPaymentDays = (effectiveStartDate: string) => {
    const effectiveDate = new Date(effectiveStartDate);

    if (isNaN(effectiveDate.getTime())) return undefined;

    const semiMonthlyConfig = getConfigByFrequency(
      'semiMonthly',
      config?.flexiblePayments?.paymentFrequencies
    ) as Array<SelectOption<Array<number>>>;

    const effectiveDay = effectiveDate.getUTCDate();

    // There are scenarios where could exist duplicated values
    // 1,15 and 15,29, in this example day 15 is the duplicated value,
    // if that is the case we want to return the first instance where the
    // effective day is found
    const match = semiMonthlyConfig.find(option =>
      option.value.includes(effectiveDay)
    );

    if (!match) {
      const defaultDays = [1, 15];

      const defaultValue =
        semiMonthlyConfig.find(option =>
          defaultDays.some(day => option.value.includes(day))
        )?.value ?? semiMonthlyConfig[0].value;

      return defaultValue.join(',');
    }

    return match.value.join(',');
  };

  // Monthly default based on the effective date
  const getDefaultMonthlyPaymentDay = (effectiveStartDate: string) => {
    const effectiveDate = new Date(effectiveStartDate);

    if (isNaN(effectiveDate.getTime())) return undefined;

    const monthlyConfig = getConfigByFrequency(
      'monthly',
      config?.flexiblePayments?.paymentFrequencies
    ) as Array<SelectOption<number>>;

    const effectiveDay = effectiveDate.getUTCDate();

    const match = monthlyConfig.find(option => option.value === effectiveDay);

    if (!match) return undefined;

    return match.value.toString();
  };

  const getPriceCustomizerInitialValues = useCallback((): PriceCustomizerFormValues => {
    const { frequency, daysOfWeek, daysOfMonth } =
      details?.flexiblePaymentPreferences || {};

    const effectiveStartDate =
      details?.effectiveDate.value ||
      config?.effectiveDates?.startDate || // Fallback
      new Date().toISOString(); // Fallback

    const {
      frequency: initialFrequency,
      dayOfWeek = getDefaultWeeklyPaymentDay(effectiveStartDate),
      biweeklyDate = getDefaultBiweeklyPaymentDay(effectiveStartDate),
      semiMonthlyDates = getDefaultSemiMonthlyPaymentDays(effectiveStartDate),
      monthly = getDefaultMonthlyPaymentDay(effectiveStartDate),
    } = fromInputToOutput({
      frequency: toCamelCase(frequency) as FlexiblePaymentFrequency,
      daysOfWeek: daysOfWeek ? daysOfWeek.map(String) : [],
      daysOfMonth: daysOfMonth ? daysOfMonth.map(Number) : [],
    });

    return {
      frequency: initialFrequency,
      dayOfWeek,
      biweeklyDate,
      semiMonthlyDates,
      monthly,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const generateSelectorInitialState = (
    formValues: PriceCustomizerFormValues
  ): {
    status: PriceState;
    context: Partial<PriceContext>;
  } => {
    const contextInitialState = getPriceCustomizerInitialValues();

    return {
      status: 'ready',
      context: {
        biweeklyDate:
          formValues.biweeklyDate || contextInitialState.biweeklyDate,
        dayOfMonth: formValues.monthly || contextInitialState.monthly,
        dayOfWeek: formValues.dayOfWeek || contextInitialState.dayOfWeek,
        downpayment: 0,
        frequency: formValues.frequency || contextInitialState.frequency,
        semiMonthlyDates:
          formValues.semiMonthlyDates || contextInitialState.semiMonthlyDates,
      },
    };
  };

  const buildUpdatePayload = (formValues: PriceCustomizerFormValues) => {
    const { frequency, daysOfMonth, daysOfWeek } = fromOutputToInput(
      formValues
    );

    return {
      flexiblePaymentPreferences: {
        frequency: toSnakeCase(frequency as FlexiblePaymentFrequency),
        days_of_month: daysOfMonth ? daysOfMonth.map(Number) : [],
        days_of_week: daysOfWeek ? daysOfWeek.map(String) : [],
      },
    };
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.mainHeader}>Build a personalized payment plan</h2>
      <p className={styles.subHeader}>
        Customers have full control over their payment schedule—with no extra
        fees. Weekly, biweekly, or monthly—it’s totally up to them.
      </p>
      <Formik
        initialValues={getPriceCustomizerInitialValues()}
        validationSchema={validationSchema}
        onSubmit={() => {}}
        validateOnMount
      >
        {({
          handleSubmit,
          values: formValues,
          setSubmitting,
          isSubmitting,
        }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <SelectorContainer
                isLoading={rateLoading || detailsLoading || isSubmitting}
                selectorsData={{
                  frequency: buildFrequencySelectorData(),
                  weekday: buildWeekdaySelectorData(
                    config?.flexiblePayments?.paymentFrequencies
                  ),
                  biweeklyDate: buildBiweeklyDateSelectorData(
                    formValues.dayOfWeek,
                    config?.effectiveDates?.startDate
                  ),
                  semiMonthly: buildSemiMonthlySelectorData(
                    config?.flexiblePayments?.paymentFrequencies
                  ),
                  monthly: buildMonthlySelectorData(
                    config?.flexiblePayments?.paymentFrequencies
                  ),
                }}
                initialState={generateSelectorInitialState(formValues)}
                onSelectorChange={async props => {
                  setSubmitting(true);

                  const [[selectorName, value]] = Object.entries(props);

                  api
                    .saveAppData({
                      data: buildUpdatePayload({
                        ...formValues,
                        [selectorName]: value,
                      }),
                    })
                    .then(async () => {
                      await fetchRate();
                      // Refresh details to reflect the latest data generated after rate
                      await refreshDetails();
                      await getSignaturePacket();
                    })
                    .finally(() => {
                      setSubmitting(false);
                    });
                }}
              />
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
